import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Card, Checkbox, Col, Divider, Form, Input, Modal,
  Radio, Row, Select, Space, Tag, Typography, message
} from 'antd';
import {
  BankOutlined, CheckCircleOutlined, HomeOutlined, IdcardOutlined,
  LockOutlined, LoginOutlined, MailOutlined, PhoneOutlined,
  SafetyCertificateOutlined, SoundOutlined, UserOutlined, WechatOutlined
} from '@ant-design/icons';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import OfficialSchoolPicker from '../components/schools/OfficialSchoolPicker';
import WeChatQRLogin from '../components/WeChatQRLogin';
import '../assets/css/LoginRegister.css';

const { Paragraph, Text, Title } = Typography;

const organizationTypeOptions = [
  { value: 'non_standard_school', label: '非标准学校' },
  { value: 'training_institution', label: '教育培训机构' },
  { value: 'social_organization', label: '社会组织' },
  { value: 'community_education', label: '社区教育机构' },
  { value: 'other_organization', label: '其他机构' }
];

function userFromWechatPayload(user) {
  return {
    id: user.id,
    username: user.username,
    name: user.name,
    displayName: user.displayName || user.name,
    role: user.role || 'teacher',
    userType: user.userType || user.role || 'teacher',
    schoolId: user.schoolId,
    schoolName: user.schoolName,
    teacherId: user.teacherId,
    teacherName: user.teacherName,
    subject: user.subject
  };
}

const LoginRegister = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setUserLogin } = useAuth();
  const [form] = Form.useForm();
  const registrationType = Form.useWatch('registrationType', form) || 'teacher';
  const registrationMethod = Form.useWatch('registrationMethod', form) || 'wechat';
  const schoolSource = Form.useWatch('schoolSource', form) || 'official';
  const [pendingInviteCode, setPendingInviteCode] = useState('');
  const [officialSchool, setOfficialSchool] = useState(null);
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registerError, setRegisterError] = useState('');
  const [qrVisible, setQrVisible] = useState(false);
  const [qrSessionKey, setQrSessionKey] = useState(0);

  useEffect(() => {
    if (location.pathname === '/login-register') {
      navigate(`/register${location.search}`, { replace: true });
      return;
    }
    const inviteCode = new URLSearchParams(location.search).get('invite_code');
    if (!inviteCode) return;
    const normalizedCode = inviteCode.trim().toUpperCase();
    if (!normalizedCode) return;
    localStorage.setItem('pendingMembershipInviteCode', normalizedCode);
    setPendingInviteCode(normalizedCode);
  }, [location.pathname, location.search, navigate]);

  useEffect(() => {
    setOfficialSchool(null);
  }, [schoolSource]);

  const buildPayload = (values, method) => {
    const payload = {
      registrationType: values.registrationType,
      displayName: values.displayName.trim(),
      email: values.email?.trim() || undefined,
      phone: values.phone?.trim() || undefined,
      subject: values.subject?.trim() || undefined,
      applicationNote: values.applicationNote?.trim() || undefined,
      invitationCode: pendingInviteCode || undefined
    };
    if (values.schoolSource === 'official') {
      payload.officialSchoolCode = officialSchool.officialSchoolCode;
      payload.schoolName = officialSchool.name;
      payload.schoolProvince = officialSchool.provinceName || undefined;
      payload.schoolCity = officialSchool.cityName || undefined;
      payload.schoolCounty = officialSchool.countyName || undefined;
      payload.schoolType = officialSchool.schoolCategoryName || undefined;
    } else {
      payload.schoolName = values.organizationName.trim();
      payload.schoolType = values.organizationType;
      payload.schoolAddress = values.organizationAddress?.trim() || undefined;
    }
    if (method === 'password') {
      payload.username = values.username.trim();
      payload.password = values.password;
    }
    return payload;
  };

  const validateRegistration = async () => {
    const values = await form.validateFields();
    if (values.schoolSource === 'official' && !officialSchool?.officialSchoolCode) {
      message.warning('请从全国标准学校数据库中选择学校');
      throw new Error('OFFICIAL_SCHOOL_REQUIRED');
    }
    return values;
  };

  const submitRegistration = async (payload, token) => {
    const response = await fetch('/api/v2/auth/register', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {})
      },
      body: JSON.stringify(payload)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.success) throw new Error(data.message || '注册失败，请稍后重试');
    return data.data || {};
  };

  const showPasswordRegistrationSuccess = (result) => {
    const pending = result.status === 'pending';
    Modal.success({
      title: pending ? '注册申请已提交' : '账号注册成功',
      content: (
        <div>
          <p>{pending ? '学校管理员权限需要平台审核，审核期间请保管好账号密码。' : '账号已经可以使用用户名和密码登录。'}</p>
          <p>登录后可在“个人资料 → 微信绑定”中绑定微信，以后即可扫码登录。</p>
        </div>
      ),
      okText: '前往登录',
      onOk: () => navigate('/login')
    });
  };

  const handlePasswordRegister = async (values) => {
    if (registrationMethod !== 'password') return;
    try {
      setRegisterLoading(true);
      setRegisterError('');
      if (schoolSource === 'official' && !officialSchool?.officialSchoolCode) {
        message.warning('请从全国标准学校数据库中选择学校');
        return;
      }
      const result = await submitRegistration(buildPayload(values, 'password'));
      showPasswordRegistrationSuccess(result);
    } catch (error) {
      setRegisterError(error.message || '注册失败，请稍后重试');
    } finally {
      setRegisterLoading(false);
    }
  };

  const prepareWechatRegistration = async () => {
    try {
      setRegisterError('');
      await validateRegistration();
      setQrSessionKey((current) => current + 1);
      setQrVisible(true);
    } catch (error) {
      if (error.message !== 'OFFICIAL_SCHOOL_REQUIRED' && !error.errorFields) {
        setRegisterError(error.message || '请先补全注册信息');
      }
    }
  };

  const handleWechatRegistrationSuccess = async (wechatUser) => {
    if (!wechatUser?.token) {
      setRegisterError('微信身份验证结果不完整，请重新扫码');
      return;
    }
    try {
      setRegisterLoading(true);
      const values = await validateRegistration();
      const result = await submitRegistration(buildPayload(values, 'wechat'), wechatUser.token);
      setQrVisible(false);
      setUserLogin(userFromWechatPayload(wechatUser), wechatUser.token);
      Modal.success({
        title: result.status === 'pending' ? '微信注册申请已提交' : '微信一键注册成功',
        content: (
          <div>
            <p>{result.status === 'pending' ? '学校管理员权限需要平台审核；微信账号已经作为登录身份保留。' : '学校信息已关联，可以使用微信扫码登录。'}</p>
            <p>建议现在到“个人资料 → 账号密码”设置备用用户名和密码。</p>
          </div>
        ),
        okText: '设置账号密码',
        onOk: () => navigate('/profile')
      });
    } catch (error) {
      setQrVisible(false);
      setRegisterError(error.message || '微信注册失败，请稍后重试');
    } finally {
      setRegisterLoading(false);
    }
  };

  return (
    <div className="registration-page">
      <div className="registration-shell">
        <div className="registration-brand">
          <Space align="center" size={12}>
            <SoundOutlined className="registration-brand-icon" />
            <div>
              <Title level={2}>教室小喇叭账号注册</Title>
              <Text>选择真实学校或机构，建立可持续使用的统一账号</Text>
            </div>
          </Space>
          <Space wrap>
            <Button type="link" icon={<HomeOutlined />} onClick={() => navigate('/')}>返回首页</Button>
            <Button icon={<LoginOutlined />} onClick={() => navigate('/login')}>已有账号，去登录</Button>
          </Space>
        </div>

        <Row gutter={[20, 20]}>
          <Col xs={24} lg={7}>
            <Card className="registration-guide-card">
              <Tag color="green" icon={<WechatOutlined />}>推荐</Tag>
              <Title level={3}>微信一键注册</Title>
              <Paragraph>扫码完成身份验证，不需要先记住一套新密码。注册后仍可在个人资料中设置账号密码作为备用登录方式。</Paragraph>
              <Divider />
              <Space direction="vertical" size="middle">
                <Text><CheckCircleOutlined /> 优先匹配全国标准学校数据库</Text>
                <Text><CheckCircleOutlined /> 非标准学校和社会机构可独立登记</Text>
                <Text><CheckCircleOutlined /> 微信与账号密码可双向绑定</Text>
                <Text><CheckCircleOutlined /> 学校管理员申请保留审核流程</Text>
              </Space>
            </Card>
          </Col>

          <Col xs={24} lg={17}>
            <Card className="registration-form-card">
              {pendingInviteCode && (
                <Alert type="success" showIcon message="已识别邀请注册" description={`邀请码 ${pendingInviteCode} 已保存，完成注册登录后会自动尝试领取对应权限。`} style={{ marginBottom: 20 }} />
              )}
              {registerError && <Alert type="error" showIcon message={registerError} style={{ marginBottom: 20 }} />}

              <Form form={form} layout="vertical" onFinish={handlePasswordRegister} initialValues={{ registrationType: 'teacher', registrationMethod: 'wechat', schoolSource: 'official' }}>
                <Title level={4}>1. 选择注册身份</Title>
                <Form.Item name="registrationType" rules={[{ required: true }]}>
                  <Radio.Group optionType="button" buttonStyle="solid" className="registration-choice-group">
                    <Radio.Button value="teacher"><UserOutlined /> 教师账号</Radio.Button>
                    <Radio.Button value="school_admin"><BankOutlined /> 学校/机构管理员</Radio.Button>
                  </Radio.Group>
                </Form.Item>
                {registrationType === 'school_admin' && <Alert type="info" showIcon message="管理员申请提交后由平台审核，审核通过后获得学校或机构管理权限。" style={{ marginBottom: 20 }} />}

                <Title level={4}>2. 选择学校或机构</Title>
                <Form.Item name="schoolSource" rules={[{ required: true }]}>
                  <Radio.Group className="school-source-grid">
                    <Radio value="official">
                      <span className="school-source-title"><SafetyCertificateOutlined /> 全国标准学校</span>
                      <span className="school-source-description">从全国标准学校数据库逐级选择，推荐普通学校使用</span>
                    </Radio>
                    <Radio value="custom">
                      <span className="school-source-title"><IdcardOutlined /> 非标准学校 / 社会机构</span>
                      <span className="school-source-description">适用于培训机构、社会组织、社区教育和未收录学校</span>
                    </Radio>
                  </Radio.Group>
                </Form.Item>

                {schoolSource === 'official' ? (
                  <div className="school-picker-panel">
                    <OfficialSchoolPicker value={officialSchool} onChange={setOfficialSchool} />
                    {officialSchool && <Alert type="success" showIcon message={`已选择：${officialSchool.name}`} description={[officialSchool.provinceName, officialSchool.cityName, officialSchool.countyName, officialSchool.schoolCategoryName].filter(Boolean).join(' · ')} style={{ marginTop: 14 }} />}
                  </div>
                ) : (
                  <Row gutter={16}>
                    <Col xs={24} md={14}>
                      <Form.Item name="organizationName" label="学校/机构全称" rules={[{ required: true, message: '请输入学校或机构全称' }, { max: 255 }]}>
                        <Input prefix={<BankOutlined />} placeholder="请填写真实完整名称" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={10}>
                      <Form.Item name="organizationType" label="机构类型" rules={[{ required: true, message: '请选择机构类型' }]}>
                        <Select options={organizationTypeOptions} placeholder="请选择" />
                      </Form.Item>
                    </Col>
                    <Col span={24}>
                      <Form.Item name="organizationAddress" label="所在地区/详细地址（选填）" rules={[{ max: 2000 }]}>
                        <Input placeholder="例如：广东省梅州市平远县……" />
                      </Form.Item>
                    </Col>
                  </Row>
                )}

                <Title level={4}>3. 填写基本信息</Title>
                <Row gutter={16}>
                  <Col xs={24} md={12}>
                    <Form.Item name="displayName" label="姓名/显示名称" rules={[{ required: true, message: '请输入姓名或显示名称' }, { max: 120 }]}>
                      <Input prefix={<UserOutlined />} placeholder="例如：张老师" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="subject" label="任教科目/负责范围（选填）" rules={[{ max: 120 }]}>
                      <Input placeholder="例如：语文、信息化管理" />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="phone" label="联系电话" rules={registrationType === 'school_admin' ? [{ required: true, message: '管理员申请需要联系电话' }, { pattern: /^1[3-9]\d{9}$/, message: '请输入有效手机号' }] : [{ pattern: /^1[3-9]\d{9}$/, message: '请输入有效手机号' }]}>
                      <Input prefix={<PhoneOutlined />} placeholder={registrationType === 'school_admin' ? '用于审核联系' : '选填'} />
                    </Form.Item>
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item name="email" label="邮箱（选填）" rules={[{ type: 'email', message: '请输入有效邮箱' }]}>
                      <Input prefix={<MailOutlined />} placeholder="用于接收通知和找回账号" />
                    </Form.Item>
                  </Col>
                  {registrationType === 'school_admin' && (
                    <Col span={24}>
                      <Form.Item name="applicationNote" label="申请说明（选填）" rules={[{ max: 1000 }]}>
                        <Input.TextArea rows={3} placeholder="可填写职务、负责范围和申请管理权限的原因" />
                      </Form.Item>
                    </Col>
                  )}
                </Row>

                <Title level={4}>4. 选择注册方式</Title>
                <Form.Item name="registrationMethod" rules={[{ required: true }]}>
                  <Radio.Group className="registration-method-grid">
                    <Radio value="wechat">
                      <span className="method-title"><WechatOutlined /> 微信一键注册 <Tag color="green">推荐</Tag></span>
                      <span className="method-description">扫码验证微信身份，注册后可再设置账号密码</span>
                    </Radio>
                    <Radio value="password">
                      <span className="method-title"><LockOutlined /> 账号密码注册</span>
                      <span className="method-description">设置用户名和密码，登录后可再绑定微信</span>
                    </Radio>
                  </Radio.Group>
                </Form.Item>

                {registrationMethod === 'password' && (
                  <Row gutter={16}>
                    <Col xs={24} md={12}>
                      <Form.Item name="username" label="登录用户名" rules={[{ required: true, message: '请输入用户名' }, { pattern: /^[A-Za-z0-9][A-Za-z0-9_.-]{2,29}$/, message: '3至30位字母、数字、下划线、点或短横线' }]}>
                        <Input prefix={<UserOutlined />} autoComplete="username" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="password" label="登录密码" rules={[{ required: true, message: '请输入密码' }, { min: 8, message: '密码至少8个字符' }, { max: 128 }]}>
                        <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
                      </Form.Item>
                    </Col>
                    <Col xs={24} md={12}>
                      <Form.Item name="confirmPassword" label="确认密码" dependencies={['password']} rules={[{ required: true, message: '请再次输入密码' }, ({ getFieldValue }) => ({ validator(_, value) { return !value || getFieldValue('password') === value ? Promise.resolve() : Promise.reject(new Error('两次输入的密码不一致')); } })]}>
                        <Input.Password prefix={<LockOutlined />} autoComplete="new-password" />
                      </Form.Item>
                    </Col>
                  </Row>
                )}

                <Form.Item name="privacyAccepted" valuePropName="checked" rules={[{ validator: (_, value) => value ? Promise.resolve() : Promise.reject(new Error('请先阅读并同意隐私政策和用户协议')) }]}>
                  <Checkbox>
                    我已阅读并同意 <a href="/privacy.html" target="_blank" rel="noreferrer">隐私政策</a> 和 <a href="/agreement.html" target="_blank" rel="noreferrer">用户协议</a>。平台仅处理注册和提供服务所必需的信息。
                  </Checkbox>
                </Form.Item>

                {registrationMethod === 'wechat' ? (
                  <Button type="primary" block size="large" icon={<WechatOutlined />} loading={registerLoading} className="wechat-register-button" onClick={prepareWechatRegistration}>
                    微信扫码一键注册（推荐）
                  </Button>
                ) : (
                  <Button type="primary" htmlType="submit" block size="large" loading={registerLoading}>使用账号密码注册</Button>
                )}
              </Form>
            </Card>
          </Col>
        </Row>
      </div>

      <Modal title={<span><WechatOutlined style={{ color: '#07c160', marginRight: 8 }} />微信一键注册</span>} open={qrVisible} onCancel={() => setQrVisible(false)} footer={null} width={520} destroyOnClose>
        <Alert type="info" showIcon message="扫码后会将微信身份与刚才填写的学校/机构信息绑定。" style={{ marginBottom: 12 }} />
        {qrVisible && <WeChatQRLogin key={qrSessionKey} mode="register" onSuccess={handleWechatRegistrationSuccess} onError={(error) => setRegisterError(error?.message || '微信验证失败，请稍后重试')} />}
      </Modal>
    </div>
  );
};

export default LoginRegister;
